import React, { useMemo, useState } from 'react';
import { useCatalogDraft } from '../../hooks/useCatalogDraft';
import { readSheet } from '../../catalog/readSheet';
import { MAX_IMPORT_BYTES, importTooLargeMessage } from '../../catalog/importLimits';
import { inferMapping, headerToQuarter } from '../../catalog/columnMapping';
import { normalizeCostWorksheet, isCostWorksheet } from '../../catalog/costWorksheet';
import { getCurrentQuarter } from '../../constants/quarterConfig';
import { ReviewPanel } from './ReviewPanel';

/**
 * The CSV entry point (design spec, import flow): the PM picks a spreadsheet,
 * confirms which column means what, and lands on `ReviewPanel` (Task 10) —
 * the same screen the manual editor lands on, so both entry points share one
 * set of "is this safe to write" rules.
 *
 * Three steps, each earning its keep:
 *
 * 1. Upload — read via `FileReader.readAsText`, never `file.text()`: jsdom
 *    16.7 implements `File`/`FileReader` but not `Blob.prototype.text()`, so
 *    `await file.text()` throws under jest while working in every browser.
 * 2. Mapping — pre-filled from the saved import profile and header inference
 *    (Task 3 / Task 8), so re-mapping a dozen quarter columns by hand every
 *    quarter is not how a cost lands in the wrong one. Only the ref column
 *    and the quarter columns are asked about; everything else is either
 *    placed automatically or explicitly named as ignored — never silent.
 * 3. `ReviewPanel` — rendered once `stage()` has produced a review.
 */

// The probe call: an empty ref column and no quarter columns, so no header
// can be a mapped column and this call can never throw. Blank headers are
// tolerated by readSheet's own duplicate check and come back in
// `ignoredColumns` along with every other header, which is how the page
// learns the full header row through readSheet's own (quote-aware) split,
// rather than a second, hand-rolled CSV parser that could disagree with it.
const EMPTY_MAPPING = { refColumn: '', quarterColumns: {} };

/** A blank header is a real column that cannot be picked by name. Named by
 *  position everywhere the page lists headers, so "ignored: , ," never
 *  appears. */
const columnLabel = (h, i) => (h === '' ? `(blank column ${i + 1})` : h);

/** inferred + saved profile -> one mapping, profile winning field-by-field.
 *  Quarter columns merge key-by-key (inference first, then the profile's own
 *  entries laid on top) so a header the profile never mentions still gets
 *  inference's answer, and a header the profile DOES cover keeps its saved
 *  target even where that happens to agree with inference. A profile with a
 *  corrupt `mapping` (`useCatalogDraft.js`'s `toProfile` degrades it to
 *  `null` rather than throwing) costs only the pre-fill, never the page. */
function mergeMapping(inferred, profile) {
  const fromProfile = profile && profile.mapping ? profile.mapping : null;
  return {
    refColumn: (fromProfile && fromProfile.refColumn) || inferred.refColumn,
    quarterColumns: { ...inferred.quarterColumns, ...(fromProfile ? fromProfile.quarterColumns : {}) },
  };
}

export function ImportPage({ vendor, user }) {
  const {
    status, profile, components, stage, review, commit, resolveAlias, saveProfile,
    reportUploadError, error,
  } = useCatalogDraft(vendor);

  const [headers, setHeaders] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [sheetText, setSheetText] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cellErrors, setCellErrors] = useState([]);
  const [worksheetSummary, setWorksheetSummary] = useState(null);

  const componentSkus = useMemo(
    () => [...new Set((components ?? []).map((c) => c.componentSku))],
    [components],
  );

  // Options offered by every per-column quarter picker — the union of every
  // quarter ANY header in this sheet looks like, not just that header's own
  // guess, so a column named unlike the rest ('Jan-Mar 2026') can still be
  // pointed at a real quarter by hand instead of being stuck on Ignore.
  const allQuarters = useMemo(
    () => (headers ? [...new Set(headers.map(headerToQuarter).filter(Boolean))] : []),
    [headers],
  );

  // Silence about an unmapped column is how a whole quarter goes missing —
  // named here, unconditionally, the moment a sheet is parsed, not only when
  // something goes on to fail.
  const ignoredColumns = headers && mapping
    ? headers.map((h, i) => ({ h, i }))
        .filter(({ h }) => h !== mapping.refColumn && !(h in mapping.quarterColumns))
        .map(({ h, i }) => columnLabel(h, i))
    : [];

  const canProceed = Boolean(mapping && mapping.refColumn);

  const resetUpload = () => {
    setHeaders(null);
    setMapping(null);
    setSheetText(null);
    setFileName(null);
    setCellErrors([]);
    setWorksheetSummary(null);
  };

  /** Parse `text` as the generic sheet and put the mapping form on screen.
   *  `useProfile: false` is for a source whose columns are already known
   *  (the cost worksheet): a profile saved from some earlier hand-made
   *  sheet must not rename its ref column. */
  const ingest = (text, name, { useProfile }) => {
    const { ignoredColumns: allHeaders } = readSheet(text, EMPTY_MAPPING, vendor, name);
    const inferred = inferMapping(allHeaders);
    const merged = useProfile ? mergeMapping(inferred, profile) : inferred;
    // THIS is the call that can throw — a genuinely duplicated ref or
    // quarter column (readSheet.ts). Validated eagerly, here, so the PM sees
    // the message before touching a single dropdown.
    readSheet(text, merged, vendor, name);
    setHeaders(allHeaders);
    setMapping(merged);
    setSheetText(text);
    setFileName(name);
  };

  /** Shared by both file inputs. `transform(raw)` returns `{ text, summary }`;
   *  the generic path is the identity, the worksheet path normalises first.
   *  Anything either step throws lands in reportUploadError, the only place
   *  a FileReader callback's error can reach the screen. */
  const readFile = (event, transform) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // A file left over from a PREVIOUS attempt — successful or not — must not
    // linger next to this one: a standing review is still committable
    // (useCatalogDraft.js's reportUploadError doc comment), and a stale
    // mapping form for the old file would answer questions about the new one.
    reportUploadError(null);
    resetUpload();

    // CONF-242: refuse an oversized file before the FileReader touches it. The
    // message goes where every other upload error goes.
    if (file.size > MAX_IMPORT_BYTES) {
      reportUploadError(new Error(importTooLargeMessage(file.name, file.size)));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      reportUploadError(reader.error ?? new Error(`Could not read ${file.name}.`));
    };
    reader.onload = () => {
      const raw = typeof reader.result === 'string' ? reader.result : '';
      try {
        const { text, summary } = transform(raw);
        ingest(text, file.name, { useProfile: summary === null });
        setWorksheetSummary(summary);
      } catch (err) {
        reportUploadError(err);
      }
    };
    reader.readAsText(file);
  };

  const handlePricesFile = (event) => readFile(event, (raw) => {
    if (isCostWorksheet(raw)) {
      throw new Error('This file looks like a cost worksheet — use "Import cost worksheet" instead.');
    }
    return { text: raw, summary: null };
  });

  // The cost worksheet is a known shape (costWorksheet.ts): normalise it to
  // the generic sheet FIRST, then run exactly the same mapping and review.
  // From the current quarter onward — earlier columns are history, and
  // re-authoring them would rewrite already-quoted prices.
  const handleWorksheetFile = (event) => readFile(event, (raw) => {
    const { csv, summary } = normalizeCostWorksheet(raw, { fromQuarter: getCurrentQuarter() });
    return { text: csv, summary };
  });

  // A header can name at most one thing. Picking it as the ref column drops
  // it from the quarter map so the two pickers can never disagree about it.
  const setRefColumn = (value) => {
    setMapping((m) => {
      if (!m) return m;
      const quarterColumns = { ...m.quarterColumns };
      delete quarterColumns[value];
      return { refColumn: value, quarterColumns };
    });
  };

  const setQuarterFor = (header, quarter) => {
    setMapping((m) => {
      if (!m) return m;
      const quarterColumns = { ...m.quarterColumns };
      if (quarter) quarterColumns[header] = quarter; else delete quarterColumns[header];
      return { ...m, quarterColumns };
    });
  };

  const handleContinue = () => {
    if (!canProceed || !sheetText) return;
    try {
      // A cell-error list from a PREVIOUS Continue attempt must not survive
      // this one: if THIS attempt throws (duplicate mapping) before reaching
      // readSheet's own errors, a stale list would still be on screen,
      // describing cells from the wrong attempt.
      setCellErrors([]);
      // Re-derived from the CURRENT mapping, not whatever upload-time first
      // computed — the PM may have just corrected the ref column or reassigned
      // a quarter, and stage() must see that correction, not the stale guess.
      const { draft, errors } = readSheet(sheetText, mapping, vendor, fileName);
      setCellErrors(errors);
      stage(draft);
    } catch (err) {
      // Reachable only if the PM's own edits re-created a duplicate mapping
      // (e.g. pointing two distinct headers at a role readSheet then finds
      // collides) — the upload-time check above cannot see edits made after it.
      reportUploadError(err);
    }
  };

  // Mirrors EditionsPage.jsx's own pattern: `busy` is a PAGE-local flag
  // around commit/resolveAlias, because useCatalogDraft's own lock is a ref,
  // not state (useCatalogDraft.js: "pages own their own busy flag for the
  // disabled attribute, as EditionsPage.jsx:17 does"). saveProfile does NOT
  // go through this: it is a fire-and-forget call with no local continuation
  // (the hook's own busy.current ref already refuses a concurrent write and
  // reports through `error`), so there is nothing here for `act()` to miss.
  const withBusy = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleSaveProfile = () => {
    if (!mapping || !mapping.refColumn) return;
    saveProfile(mapping, { actor: user?.username });
  };

  const handleCommit = () => withBusy(() => commit({ actor: user?.username }));
  const handleResolveAlias = (input) => withBusy(() => resolveAlias({ ...input, actor: user?.username }));

  if (status !== 'ready') {
    return <div>{status === 'error' ? 'Failed to load the catalog.' : 'Loading catalog…'}</div>;
  }

  return (
    <div>
      <h1 className="text-xl font-bold mb-4">Import costs — {vendor}</h1>

      {/* Once a review exists, ReviewPanel owns showing `error` beside it —
          a top-level banner AND the panel's own would say the same thing
          twice. Before that, this is the only place an upload/staging
          failure can surface. */}
      {!review && error && (
        <div className="text-red-400 mb-3" role="alert">{error}</div>
      )}

      <div className="mb-4 flex flex-wrap gap-8">
        <div>
          <label htmlFor="catalog-import-file" className="block text-sm text-gray-400 mb-1">
            Import prices (CSV)
          </label>
          <input
            id="catalog-import-file"
            type="file"
            accept=".csv,text/csv"
            onChange={handlePricesFile}
          />
          <p className="text-gray-500 text-xs mt-1">Column 1 is the part number; the other columns are quarters.</p>
        </div>

        <div>
          <label htmlFor="catalog-import-worksheet" className="block text-sm text-gray-400 mb-1">
            Import cost worksheet (CSV)
          </label>
          <input
            id="catalog-import-worksheet"
            type="file"
            accept=".csv,text/csv"
            onChange={handleWorksheetFile}
          />
          <p className="text-gray-500 text-xs mt-1">
            The quarterly Operations Tracker cost worksheet, as sent. Split into costs and prices automatically.
          </p>
        </div>
      </div>

      {worksheetSummary && (
        <div data-testid="worksheet-summary" className="mb-4 text-sm text-gray-300">
          <p>
            Cost worksheet: {worksheetSummary.rows} rows from {worksheetSummary.fromQuarter} onward
            ({worksheetSummary.quarters.join(', ')}).
          </p>
          {worksheetSummary.unnumbered.length > 0 && (
            <p>
              Imported {worksheetSummary.unnumbered.length} {worksheetSummary.unnumbered.length === 1 ? 'row' : 'rows'} with
              no Vdura PN as catalog components: {worksheetSummary.unnumbered.map((u) => `${u.label} → ${u.sku}`).join('; ')}.
            </p>
          )}
          {worksheetSummary.skippedNoPn > 0 && (
            <p>Skipped {worksheetSummary.skippedNoPn} {worksheetSummary.skippedNoPn === 1 ? 'row' : 'rows'} with no Vdura PN.</p>
          )}
          {worksheetSummary.ignored.length > 0 && (
            <p>Ignored: {worksheetSummary.ignored.map((i) => `${i.ref} (${i.reason})`).join('; ')}</p>
          )}
        </div>
      )}

      {headers && mapping && (
        <div className="mb-4">
          <div className="mb-3">
            <label htmlFor="mapping-ref-column" className="block text-sm text-gray-400 mb-1">
              Ref column
            </label>
            <select
              id="mapping-ref-column"
              value={mapping.refColumn}
              onChange={(e) => setRefColumn(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
            >
              <option value="">— choose —</option>
              {/* A blank header can never be a mapped column (design spec) — two
                  blank columns would both key mapping.quarterColumns/refColumn by
                  the same '' string and silently collide. Only non-blank headers
                  are offered here; blank ones still appear, unselectable, below. */}
              {headers.filter((h) => h !== '').map((h, i) => <option key={`${h}#${i}`} value={h}>{columnLabel(h, i)}</option>)}
            </select>
          </div>

          <div className="mb-3">
            <h2 className="font-bold text-sm text-gray-400 mb-2">Quarter columns</h2>
            {headers.map((h, i) => ({ h, i })).filter(({ h }) => h !== mapping.refColumn).map(({ h, i }) => (
              <div key={`${h}#${i}`} className="flex items-center gap-2 mb-1 text-sm">
                <span className="w-32 truncate">{columnLabel(h, i)}</span>
                {h === '' ? (
                  <span className="text-gray-500 italic">cannot be mapped</span>
                ) : (
                  <select
                    aria-label={columnLabel(h, i)}
                    value={mapping.quarterColumns[h] ?? ''}
                    onChange={(e) => setQuarterFor(h, e.target.value)}
                    className="bg-gray-900 border border-gray-700 rounded px-1 py-0.5"
                  >
                    <option value="">Not mapped</option>
                    {allQuarters.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-xs mb-3">
            Columns that will be ignored: {ignoredColumns.length ? ignoredColumns.join(', ') : 'none'}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canProceed}
              onClick={handleContinue}
              className="bg-orange-600 disabled:opacity-50 px-3 py-1 rounded"
            >
              Continue to review
            </button>
            {/* A worksheet's mapping is fixed by normalizeCostWorksheet (SKU +
                canonical quarter keys) and never a hand-made mapping worth
                remembering — saving it here would overwrite the generic
                profile with that shape, which then misapplies to the next
                ordinary sheet. */}
            {!worksheetSummary && (
              <button
                type="button"
                disabled={busy || !mapping.refColumn}
                onClick={handleSaveProfile}
                className="px-3 py-1 rounded border border-gray-600 hover:bg-gray-800 disabled:opacity-50"
              >
                Save mapping as profile
              </button>
            )}
          </div>
        </div>
      )}

      {cellErrors.length > 0 && (
        <div role="status" className="text-yellow-400 text-sm mb-3">
          {cellErrors.length} {cellErrors.length === 1 ? 'cell was' : 'cells were'} skipped because
          they were not amounts:{' '}
          {cellErrors.slice(0, 20).map((e) => `${e.ref} ${e.quarter} "${e.raw}"`).join(', ')}
          {cellErrors.length > 20 ? `, …and ${cellErrors.length - 20} more` : ''}
        </div>
      )}

      {review && (
        <ReviewPanel
          review={review}
          componentSkus={componentSkus}
          onCommit={handleCommit}
          onResolveAlias={handleResolveAlias}
          busy={busy}
          error={error}
        />
      )}
    </div>
  );
}

export default ImportPage;
